import React from "react";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { DashboardLayout } from "@/components/DashboardLayout";
import { StatCard, LoadingBlock, DemoTag, Card } from "@/components/common";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { euro } from "@/lib/format";
import { Users, Stethoscope, ShieldAlert, ListChecks, CalendarCheck, BadgeEuro, CheckCircle2, MessageCircle, CreditCard } from "lucide-react";

const AXIS_TICK = { fontSize: 11, fill: "#5C6B68" };
const BAR_RADIUS = [6, 6, 0, 0];

export default function AdminDashboard() {
  const { data, isLoading } = useQuery({ queryKey: ["admin-stats"], queryFn: async () => (await api.get("/admin/stats")).data });

  return (
    <DashboardLayout title="Dashboard amministratore">
      <div className="flex items-center gap-3 mb-6"><h2 className="text-2xl font-bold text-[#102A2A]">Panoramica piattaforma</h2><DemoTag /></div>
      {isLoading ? <LoadingBlock /> : (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard icon={Users} label="Pazienti totali" value={data.patients} testid="admin-stat-patients" />
          <StatCard icon={Stethoscope} label="Professionisti" value={data.professionals} tone="dark" testid="admin-stat-pros" />
          <StatCard icon={ShieldAlert} label="Da verificare" value={data.to_verify} tone="yellow" testid="admin-stat-toverify" />
          <StatCard icon={ListChecks} label="Richieste aperte" value={data.open_requests} tone="yellow" testid="admin-stat-open" />
          <StatCard icon={CalendarCheck} label="Prenotazioni confermate" value={data.confirmed_bookings} testid="admin-stat-confirmed" />
          <StatCard icon={CheckCircle2} label="Prestazioni completate" value={data.completed} testid="admin-stat-completed" />
          <StatCard icon={BadgeEuro} label="Ricavi simulati" value={euro(data.simulated_revenue_cents)} tone="dark" testid="admin-stat-revenue" />
          <StatCard icon={BadgeEuro} label="Commissione" value={`${data.commission_percent}%`} testid="admin-stat-commission" />
          <StatCard icon={MessageCircle} label="Richieste WhatsApp avviate" value={data.whatsapp_clicks ?? 0} tone="yellow" testid="admin-stat-whatsapp" />
          <StatCard icon={BadgeEuro} label="Totale tariffe concordate" value={euro(data.agreed_total_cents || 0)} tone="dark" testid="admin-stat-agreed-total" />
          <StatCard icon={ListChecks} label="Richieste con tariffa" value={data.agreed_count ?? 0} testid="admin-stat-agreed-count" />
          <StatCard icon={CreditCard} label="Modalità pagamento" value={data.payment_mode === "off" ? "Senza pagamento" : "Con pagamento"} testid="admin-stat-paymode" />
        </div>
      )}
      <WhatsappReport />
    </DashboardLayout>
  );
}

function WhatsappReport() {
  const { data } = useQuery({ queryKey: ["whatsapp-report"], queryFn: async () => (await api.get("/admin/whatsapp-report")).data });
  const perDay = (data?.per_day || []).map((d) => ({ ...d, label: d.date.slice(5) }));
  const perMuni = data?.per_municipality || [];
  return (
    <div className="mt-8 grid grid-cols-1 lg:grid-cols-3 gap-6">
      <Card className="lg:col-span-2" data-testid="whatsapp-report-chart">
        <h3 className="font-bold text-[#102A2A] mb-4">Avvii WhatsApp — ultimi 14 giorni</h3>
        <div style={{ width: "100%", height: 240 }}>
          <ResponsiveContainer>
            <BarChart data={perDay}>
              <CartesianGrid strokeDasharray="3 3" stroke="#EAF3EF" />
              <XAxis dataKey="label" tick={AXIS_TICK} />
              <YAxis allowDecimals={false} tick={AXIS_TICK} />
              <Tooltip />
              <Bar dataKey="count" name="Avvii" fill="#087A73" radius={BAR_RADIUS} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </Card>
      <Card data-testid="whatsapp-report-muni">
        <h3 className="font-bold text-[#102A2A] mb-4">Per comune</h3>
        <div className="space-y-2 max-h-64 overflow-y-auto">
          {perMuni.length === 0 ? <p className="text-sm text-[#5C6B68]">Nessun avvio registrato.</p> :
            perMuni.map((m) => (
              <div key={m.municipality} className="flex items-center justify-between text-sm border-b border-[#EAF3EF] pb-1">
                <span className="text-[#102A2A]">{m.municipality}</span>
                <span className="font-bold text-[#087A73]">{m.count}</span>
              </div>
            ))}
        </div>
      </Card>
    </div>
  );
}
